import { Capacitor } from "@capacitor/core";
import { Keyboard } from "@capacitor/keyboard";
import { SplashScreen } from "@capacitor/splash-screen";
import { Preferences } from "@capacitor/preferences";
import * as Sentry from "@sentry/capacitor";
import "core-js/stable"; // tslint:disable-line:no-import-side-effect
import "intersection-observer"; // tslint:disable-line:no-import-side-effect
import ons from "onsenui";
import React from "react";
import * as ReactDOM from "react-dom";
import "setimmediate"; // tslint:disable-line:no-import-side-effect
import { API, AuthInfo, AuthInfoV1, AuthInfoV2, migrateTokenToJWT } from "./API";
import AuthActivity from "./components/Auth/AuthActivity";
import * as legacyStorageUtils from "./cordova_storage_utils";
import * as google_maps_api from "./google_maps_api";
import * as LocalStore from "./LocalStore";
import { CaregiverId } from "./messages";
import { OutdatedVersionScreen } from "./OutdatedVersionScreen";
import { PIN_CODE_KEY, PIN_CODE_KEY_OVERRIDE } from "./PinUnlockOverlayManager";
import { getLocalStorageItem, removeLocalStorageItem, setLocalStorageItem } from "./storage_utils";
import { caregiverChatStrategies } from "./system/AppState/ChatState";
import { UserLockedScreen } from "./UserLockedScreen";
import { AppVersion } from "./version";

import "@js-joda/timezone/dist/js-joda-timezone-10-year-range";

// tslint:disable-next-line: no-import-side-effect
import "./all.scss";
import App from "./App";
import analyticsUtils from "./utils/analyticsUtils";
import { registerCodePushListener } from "./codepush";

if (!("scrollBehavior" in document.documentElement.style)) {
  import("scroll-behavior-polyfill");
}

// This must be called before `ons.ready`:
ons.disableAutoStatusBarFill();

async function main() {
  console.log("main");

  google_maps_api.load();

  await migrateNativeStorageToCapacitorStorage();

  if (ons.platform.isIOS()) {
    fixIosCursorBug();
  }

  if (isAppProduction()) {
    registerCodePushListener();
    initSentry();
    startSignup();
  }

  if (isAppDevelopment()) {
    startSignup();
  }

  if (Capacitor.isPluginAvailable("SplashScreen")) {
    SplashScreen.hide();
  }
}

main();

function getRootNode(): HTMLElement {
  const rootId = "root";
  const root = document.getElementById(rootId);
  if (root === null) {
    throw new Error('The Impossible Happened. Element with id "' + rootId + '" does not exist');
  }
  return root;
}

const rootNode = getRootNode();

function isAppProduction(): boolean {
  return import.meta.env.PROD;
}

function isAppDevelopment(): boolean {
  return import.meta.env.DEV;
}

/**
 * Used for Sentry
 */
export function setLoggedInUser(caregiverId: CaregiverId | null, _justLoggedIn: boolean): void {
  if (!isAppProduction()) {
    return;
  }

  if (caregiverId !== null) {
    const userid: string = "CaregiverId:" + CaregiverId.unwrap(caregiverId);

    // Sentry:
    Sentry.setUser({
      username: userid,
    });
  }
}

function initSentry() {
  Sentry.init({
    dsn: "https://877c6b3fa7414c23b4d1503c7bd4cac0@o64308.ingest.sentry.io/161261",
    release: AppVersion,
    extra: {
      app_release: AppVersion,
    },
  });
}

export async function startSignup() {
  // This const is defined twice because it became undefined for some reason
  // If you manage to make this work with a single const feel free to delete this
  const SAVED_AUTH_KEY = "medflyt_app_save_auth";

  if ((window as any).authInfo) {
    await setLocalStorageItem(SAVED_AUTH_KEY, JSON.stringify((window as any).authInfo));
  }

  const apiConfig = await LocalStore.loadAPIConfig();

  const savedAuth = await (async (): Promise<AuthInfoV2 | null> => {
    try {
      const unparsed = await getLocalStorageItem(SAVED_AUTH_KEY);

      if (unparsed === null) {
        return null;
      }

      const parsed: AuthInfo = JSON.parse(unparsed);

      const isLegacyAuthData = (value: AuthInfo): value is AuthInfoV1 => {
        return !("accessJWT" in value);
      };

      if (isLegacyAuthData(parsed)) {
        console.log("[auth] found legacy token. converting to new format");
        const loginData = await migrateTokenToJWT(apiConfig, parsed.authToken);

        if (loginData === null || !("tokens" in loginData)) {
          console.log(`[auth] failed to migrate token (${JSON.stringify(loginData)})`);
          await removeLocalStorageItem(SAVED_AUTH_KEY);
          return null;
        }

        const newAuth: AuthInfoV2 = {
          accessJWT: loginData.tokens.accessJWT,
          refreshJWT: loginData.tokens.refreshJWT,
        };

        await setLocalStorageItem(SAVED_AUTH_KEY, JSON.stringify(newAuth));

        return newAuth;
      }

      return parsed;
    } catch (e) {
      console.error(e);
      return null;
    }
  })();
  let pinCode = await getLocalStorageItem(PIN_CODE_KEY);
  const pinCodeOverride = sessionStorage.getItem(PIN_CODE_KEY_OVERRIDE);

  if (pinCodeOverride !== null) {
    pinCode = pinCodeOverride;
  }

  if (savedAuth !== null) {
    const passthrough = LocalStore.getPassthroughTokenQueryParamOrNull();

    if (passthrough !== null && passthrough !== "") {
      return logout();
    }

    const api = API.Load(apiConfig, savedAuth, Capacitor.getPlatform());

    setLoggedInUser(api.authInfo.caregiverId, false);
    mainScreen(api, pinCode, false, false, false, true);
  } else {
    const predefinedPhoneNumber = getPredefinedPhoneNumberFromQs();

    if (predefinedPhoneNumber !== null) {
      // Clearing cache because a new user is logging in
      await clearCache();
      analyticsUtils.init();
    }

    const handleComplete: React.ComponentProps<typeof AuthActivity>["onComplete"] = (params) => {
      ReactDOM.unmountComponentAtNode(rootNode);
      setLocalStorageItem(SAVED_AUTH_KEY, params.api.save());
      setTimeout(
        () =>
          mainScreen(
            params.api,
            "FIRST_TIME",
            true,
            params.isNewCaregiver,
            true,
            params.shouldSkipPromo
          ),
        0
      );
    };

    ReactDOM.render(
      <AuthActivity
        onLogin={(api) => setLoggedInUser(api.authInfo.caregiverId, true)}
        onComplete={handleComplete}
        onVersionOutdated={renderAppNeedsUpdate}
        onUserLocked={renderUserLocked}
      />,
      rootNode
    );
  }
}

function mainScreen(
  api: API,
  pinCode: string | null,
  isAfterRegistration: boolean,
  startWithRequestAgencies: boolean,
  initialShouldMainScreenUpdate: boolean,
  shouldShowPromotionalPopover: boolean
) {
  ReactDOM.render(
    <App
      initialShouldMainScreenUpdate={initialShouldMainScreenUpdate}
      api={api}
      chatSystemStrategies={caregiverChatStrategies(api.authInfo.caregiverId)}
      pinCode={pinCode}
      isAfterRegistration={isAfterRegistration}
      onUserLocked={() => renderUserLocked(localStorage.phoneNumber)}
      startWithRequestAgencies={startWithRequestAgencies}
      onLogout={logout}
      onAppNeedsUpdate={() => renderAppNeedsUpdate()}
      shouldShowPromotionalPopover={shouldShowPromotionalPopover}
    />,
    document.getElementById("root")
  );

  // Hide the splash screen when the app loads
  SplashScreen.hide();
}

function getPredefinedPhoneNumberFromQs() {
  const AUTO_LOGIN_PHONE_NUMBER_QS = "phoneNumber";
  const searchParams = new URLSearchParams(window.location.search);
  return searchParams.get(AUTO_LOGIN_PHONE_NUMBER_QS);
}

async function clearCache() {
  // This const is defined twice because it became undefined for some reason
  // If you manage to make this work with a single const feel free to delete this
  const SAVED_AUTH_KEY = "medflyt_app_save_auth";

  setLoggedInUser(null, true);

  delete localStorage.phoneNumber;

  ReactDOM.unmountComponentAtNode(rootNode);
  await removeLocalStorageItem(SAVED_AUTH_KEY);
  sessionStorage.removeItem(PIN_CODE_KEY_OVERRIDE);
}

async function logout() {
  await clearCache();
  startSignup();
}

function renderAppNeedsUpdate() {
  ReactDOM.unmountComponentAtNode(rootNode);
  ReactDOM.render(React.createElement(OutdatedVersionScreen, {}), rootNode);
}

function renderUserLocked(phoneNumber: string) {
  ReactDOM.unmountComponentAtNode(rootNode);
  ReactDOM.render(
    React.createElement(UserLockedScreen, { caregiverPhoneNumber: phoneNumber }),
    rootNode
  );
}

// TODO - Check if it's needed
function fixIosCursorBug() {
  const FIX_CLASSNAME = "ios-scroll-fix";

  if (!Capacitor.isPluginAvailable("Keyboard")) {
    return;
  }

  Keyboard.addListener("keyboardWillShow", () => {
    const style = document.createElement("style");
    style.className = FIX_CLASSNAME;
    style.innerHTML = "* { -webkit-overflow-scrolling: auto !important; } ";
    document.getElementsByTagName("body")[0].appendChild(style);
  });

  Keyboard.addListener("keyboardDidHide", () => {
    const elems = document.getElementsByClassName(FIX_CLASSNAME);

    while (elems.length !== 0) {
      const currElem = elems.item(0);

      if (currElem !== null) {
        currElem.remove();
      }
    }
  });
}

/**
 * We should remove this function until at least 90% of the active users will migrate to v4.0.0
 * @since 4.0.0
 */
async function migrateNativeStorageToCapacitorStorage() {
  const nativeStorage = (window as any).NativeStorage;

  if (
    !Capacitor.isNativePlatform() ||
    nativeStorage === undefined ||
    (await Preferences.keys()).keys.length > 0
  ) {
    return;
  }

  try {
    const nativeStorageKeys = await new Promise<string[]>((resolve) => {
      return (window as any).NativeStorage.keys(resolve);
    });

    const nativeStorageData = await Promise.all(
      nativeStorageKeys.map(async (key) => ({
        key: key,
        value: await legacyStorageUtils.getLocalStorageItem(key),
      }))
    );

    await Promise.all(
      nativeStorageData.map(({ key, value }) =>
        value === null ? Promise.resolve() : Preferences.set({ key, value })
      )
    );

    console.log("Migrated native storage to Capacitor storage");
  } catch {
    console.log("Failed to migrate native storage to Capacitor storage");
  }
}
